import React, { ComponentProps, ElementType } from 'react';
import { useDropdownContext } from './Dropdown';
import MenuItem, { MenuItemProps } from '../utils/MenuItem/MenuItem';

// Publicly exposed props
export type DropdownItemProps<T extends ElementType = 'li'> = MenuItemProps & {
  /** Callback when the item is selected */
  onSelect?: (event: React.MouseEvent) => void;

  as?: T;
} & Omit<ComponentProps<T>, keyof MenuItemProps | 'component' | 'onClick'>;

/**
 * DropdownItem represents a single item in a dropdown menu.
 *
 * This component maintains API compatibility with the original pounce DropdownItem.
 * It supports polymorphic rendering through the `as` prop.
 *
 * @example
 * <DropdownItem onSelect={() => console.log('clicked')}>
 *   Edit Item
 * </DropdownItem>
 */
const DropdownItemComponent = React.forwardRef<HTMLLIElement, DropdownItemProps>(
  function DropdownItem({ children, onSelect = () => {}, disabled, ...rest }, ref) {
    const { handleClose } = useDropdownContext();

    const handleClick = (event: React.MouseEvent) => {
      if (!disabled) {
        onSelect(event);
        handleClose();
      }
    };

    return (
      <MenuItem ref={ref} disabled={disabled} onClick={handleClick} {...rest}>
        {children}
      </MenuItem>
    );
  },
) as <T extends React.ElementType = 'li'>(
  props: DropdownItemProps<T> & { ref?: React.Ref<any> },
) => React.ReactElement;

export const DropdownItem = React.memo(DropdownItemComponent) as typeof DropdownItemComponent;
